import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Calendar as CalendarIcon, Clock, User, Mail, Phone, CreditCard, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const steps = ["Treatment", "Duration", "Date & Time", "Details", "Payment"];

// Group treatments with their duration options
const treatmentGroups = [
  {
    id: "ultimate",
    name: "Ultimate Healing Massage",
    description: "A deeply immersive, full-body healing experience with massage, vibroacoustic Tibetan bowl, Reiki, and crystal singing bowl sound bath.",
    category: "massage",
  },
  {
    id: "energy-reset",
    name: "Energy Reset",
    description: "A grounding treatment with tailored massage, vibroacoustic Tibetan bowl healing, and Reiki energy healing.",
    category: "massage",
  },
  {
    id: "full-body",
    name: "Tailored Full Body Massage",
    description: "A deeply relaxing full body massage, completely tailored to your individual needs.",
    category: "massage",
  },
  {
    id: "reiki",
    name: "Reiki Healing",
    description: "A gentle yet powerful energy healing treatment to restore balance within body and mind.",
    category: "sound_healing",
  },
  {
    id: "vibroacoustic",
    name: "Vibroacoustic Sound Healing",
    description: "A unique treatment using a Tibetan bowl placed directly on the body for deep relaxation.",
    category: "sound_healing",
  },
  {
    id: "sway",
    name: "Sway with Sound",
    description: "Float in a hammock while being guided through a soothing sound journey. Group experience.",
    category: "sway",
  },
  {
    id: "restoration",
    name: "Restoration Massage",
    description: "Professional, restorative massage designed specifically for men. Release tension, ease stress and restore your body.",
    category: "mens",
  },
];

export default function BookingPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [services, setServices] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedTreatment, setSelectedTreatment] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  const DEPOSIT_AMOUNT = 20;
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    notes: "",
  });

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, blockedRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/blocked-dates`),
        ]);
        
        setServices(servicesRes.data);
        setBlockedDates(blockedRes.data.map(d => d.date));
        
        // Handle pre-selection from URL params
        const preServiceId = searchParams.get("service");
        const preCategory = searchParams.get("category");
        
        if (preServiceId) {
          const matchedService = servicesRes.data.find(s => s.id === preServiceId);
          if (matchedService) {
            const group = treatmentGroups.find(g => 
              matchedService.name.includes(g.name === "Restoration Massage" ? "Restoration Massage" : g.name)
            ) || treatmentGroups.find(g => g.category === matchedService.category);
            if (group) {
              setSelectedTreatment(group);
              setSelectedService(matchedService);
              setCurrentStep(2);
            }
          }
        } else if (preCategory) {
          const group = treatmentGroups.find(g => g.category === preCategory);
          if (group) {
            setSelectedTreatment(group);
            setCurrentStep(1);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load booking data");
      }
    };
    fetchData();
  }, [searchParams]);

  // Fetch timeslots for the selected service
  useEffect(() => {
    if (!selectedService) {
      setTimeSlots([]);
      return;
    }
    const fetchSlots = async () => {
      try {
        const res = await axios.get(`${API}/timeslots?service_id=${selectedService.id}`);
        setTimeSlots(res.data);
      } catch (error) {
        console.error("Error fetching timeslots:", error);
      }
    };
    fetchSlots();
  }, [selectedService]);

  // Get duration options for selected treatment
  const getDurationOptions = () => {
    if (!selectedTreatment) return [];
    
    // Map treatment group to service name patterns
    const patterns = {
      "ultimate": "Ultimate Healing Massage",
      "energy-reset": "Energy Reset",
      "full-body": "Tailored Full Body Massage",
      "reiki": "Reiki",
      "vibroacoustic": "Vibroacoustic",
      "sway": "Sway",
      "restoration": "Restoration Massage",
    };
    
    const pattern = patterns[selectedTreatment.id];
    return services.filter(s => s.name.includes(pattern));
  };

  // Helper: format date to YYYY-MM-DD using local timezone (not UTC)
  const toLocalDateStr = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  // Update available slots when service or date changes
  useEffect(() => {
    if (selectedService && selectedDate) {
      const dateStr = toLocalDateStr(selectedDate);
      const filtered = timeSlots.filter(
        slot => slot.service_id === selectedService.id && 
                slot.date === dateStr && 
                slot.available_spots > 0
      );
      setAvailableSlots(filtered);
    }
  }, [selectedService, selectedDate, timeSlots]);

  // Get dates with available slots for the selected service
  const getAvailableDates = () => {
    if (!selectedService) return [];
    return [...new Set(
      timeSlots
        .filter(slot => slot.service_id === selectedService.id && slot.available_spots > 0)
        .map(slot => slot.date)
    )];
  };

  const isDateDisabled = (date) => {
    const dateStr = toLocalDateStr(date);
    const today = toLocalDateStr(new Date());
    
    if (dateStr < today) return true;
    if (blockedDates.includes(dateStr)) return true;
    
    const availableDates = getAvailableDates();
    return !availableDates.includes(dateStr);
  };

  const handleTreatmentSelect = (treatment) => {
    setSelectedTreatment(treatment);
    setSelectedService(null);
    setSelectedDate(null);
    setSelectedSlot(null);
    setCurrentStep(1);
  };

  const handleDurationSelect = (service) => {
    setSelectedService(service);
    setSelectedDate(null);
    setSelectedSlot(null);
    setCurrentStep(2);
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    setCurrentStep(3);
  };

  const handleInputChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setIsLoading(true);
    try {
      // Create booking
      const bookingRes = await axios.post(`${API}/bookings`, {
        service_id: selectedService.id,
        time_slot_id: selectedSlot.id,
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        notes: formData.notes,
      });
      
      // Create checkout session
      const checkoutRes = await axios.post(`${API}/checkout/booking`, {
        booking_id: bookingRes.data.id,
        origin_url: window.location.origin,
        payment_method: "stripe",
      });
      
      // Redirect to Stripe checkout
      window.location.href = checkoutRes.data.checkout_url;
    } catch (error) {
      console.error("Booking error:", error);
      toast.error(error.response?.data?.detail || "Failed to create booking");
      setIsLoading(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return !!selectedTreatment;
      case 1: return !!selectedService;
      case 2: return !!selectedSlot;
      case 3: return formData.name && formData.email;
      default: return false;
    }
  };

  const durationOptions = getDurationOptions();

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="booking-page">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="text-xs tracking-[0.3em] uppercase text-primary mb-4">
            Booking
          </p>
          <h1 className="heading-lg text-foreground">
            Book Your <em className="italic">Session</em>
          </h1>
        </motion.div>

        {/* Progress Steps */}
        <div className="max-w-4xl mx-auto mb-16 overflow-x-auto">
          <div className="flex items-center justify-between min-w-[500px] relative">
            {steps.map((step, index) => (
              <div key={step} className="flex flex-col items-center relative z-10">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors duration-300 ${
                    index <= currentStep
                      ? "bg-primary border-primary text-primary-foreground"
                      : "bg-background border-border text-muted-foreground"
                  }`}
                  data-testid={`step-${index}`}
                >
                  {index < currentStep ? <Check className="w-4 h-4" /> : index + 1}
                </div>
                <span className="text-[10px] mt-2 text-muted-foreground tracking-wider uppercase whitespace-nowrap">
                  {step}
                </span>
              </div>
            ))}
            <div className="absolute top-5 left-0 right-0 h-px bg-border -z-0" />
            <div
              className="absolute top-5 left-0 h-px bg-primary -z-0 transition-all duration-300"
              style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
            />
          </div>
        </div>

        {/* Step Content */}
        <div className="max-w-4xl mx-auto">
          {/* Step 1: Treatment Selection */}
          {currentStep === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="step-treatment"
            >
              <h2 className="heading-md mb-8 text-center">
                Choose Your Treatment
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {treatmentGroups.map((treatment) => (
                  <div
                    key={treatment.id}
                    onClick={() => handleTreatmentSelect(treatment)}
                    className={`p-6 border cursor-pointer transition-all duration-300 ${
                      selectedTreatment?.id === treatment.id
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    }`}
                    data-testid={`treatment-option-${treatment.id}`}
                  >
                    <h3 className="font-serif text-xl mb-2">{treatment.name}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {treatment.description}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 2: Duration Selection */}
          {currentStep === 1 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="step-duration"
            >
              <h2 className="heading-md mb-4 text-center">
                {selectedTreatment?.name}
              </h2>
              <p className="text-center text-muted-foreground mb-8 max-w-xl mx-auto">
                {selectedTreatment?.description}
              </p>
              <p className="text-center text-primary text-sm mb-8">
                Choose your session length
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-3xl mx-auto">
                {durationOptions.map((service) => (
                  <div
                    key={service.id}
                    onClick={() => handleDurationSelect(service)}
                    className={`p-6 border cursor-pointer transition-all duration-300 text-center ${
                      selectedService?.id === service.id
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    }`}
                    data-testid={`duration-option-${service.duration_minutes}`}
                  >
                    <div className="flex items-center justify-center gap-2 mb-3 text-primary">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        {service.duration_minutes} mins
                      </span>
                    </div>
                    <div className="font-serif text-3xl text-foreground">
                      £{service.price.toFixed(0)}
                    </div>
                    {service.max_capacity > 1 && (
                      <p className="text-xs text-muted-foreground mt-2">
                        per person • max {service.max_capacity}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 3: Date & Time Selection */}
          {currentStep === 2 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="step-datetime"
            >
              <h2 className="heading-md mb-8 text-center">
                Choose Date & Time
              </h2>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* Calendar */}
                <div className="flex justify-center">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => {
                      setSelectedDate(date);
                      setSelectedSlot(null);
                    }}
                    disabled={isDateDisabled}
                    className="rounded-none border border-border booking-calendar"
                    data-testid="booking-calendar"
                  />
                </div>

                {/* Time Slots */}
                <div>
                  <h3 className="font-serif text-xl mb-4">
                    {selectedDate
                      ? `Available times on ${selectedDate.toLocaleDateString("en-GB", {
                          weekday: "long",
                          day: "numeric",
                          month: "long",
                        })}`
                      : "Select a date to see available times"}
                  </h3>
                  
                  {selectedDate && availableSlots.length > 0 ? (
                    <div className="grid grid-cols-2 gap-3">
                      {availableSlots.map((slot) => (
                        <Button
                          key={slot.id}
                          variant={selectedSlot?.id === slot.id ? "default" : "outline"}
                          className={`rounded-none py-6 ${
                            selectedSlot?.id === slot.id
                              ? "bg-primary text-primary-foreground"
                              : "border-border hover:border-primary"
                          }`}
                          onClick={() => handleSlotSelect(slot)}
                          data-testid={`time-slot-${slot.time}`}
                        >
                          {slot.time}
                          {selectedService?.max_capacity > 1 && (
                            <span className="ml-2 text-xs opacity-70">
                              ({slot.available_spots} spots)
                            </span>
                          )}
                        </Button>
                      ))}
                    </div>
                  ) : selectedDate ? (
                    <p className="text-muted-foreground">
                      No available times on this date. Please select another day.
                    </p>
                  ) : null}
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 4: Personal Details */}
          {currentStep === 3 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="step-details"
            >
              <h2 className="heading-md mb-8 text-center">
                Your Details
              </h2>
              
              <div className="max-w-md mx-auto space-y-6">
                <div>
                  <Label htmlFor="name" className="text-sm mb-2 block">
                    Full Name *
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your name"
                      className="pl-10 rounded-none border-border"
                      required
                      data-testid="input-name"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="text-sm mb-2 block">
                    Email *
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                      className="pl-10 rounded-none border-border"
                      required
                      data-testid="input-email"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="phone" className="text-sm mb-2 block">
                    Phone (optional)
                  </Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Your phone number"
                      className="pl-10 rounded-none border-border"
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="notes" className="text-sm mb-2 block">
                    Notes (optional)
                  </Label>
                  <Textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Any specific requests or health conditions we should know about?"
                    className="rounded-none border-border min-h-[100px]"
                    data-testid="input-notes"
                  />
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 5: Payment Summary */}
          {currentStep === 4 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="step-payment"
            >
              <h2 className="heading-md mb-8 text-center">
                Confirm & Pay
              </h2>
              
              <div className="max-w-md mx-auto">
                <div className="bg-muted p-8 border border-border mb-8">
                  <h3 className="font-serif text-xl mb-6">Booking Summary</h3>
                  
                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Treatment</span>
                      <span className="font-medium text-right">{selectedTreatment?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Duration</span>
                      <span className="font-medium">{selectedService?.duration_minutes} mins</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Date</span>
                      <span className="font-medium">
                        {selectedDate?.toLocaleDateString("en-GB", {
                          weekday: "long",
                          day: "numeric",
                          month: "long",
                        })}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Time</span>
                      <span className="font-medium">{selectedSlot?.time}</span>
                    </div>
                  </div>
                  
                  <div className="border-t border-border pt-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Treatment price</span>
                      <span className="font-medium">
                        £{selectedService?.price.toFixed(0)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-primary">
                      <span className="font-medium">Deposit to pay now</span>
                      <span className="font-serif text-2xl font-medium">
                        £{DEPOSIT_AMOUNT}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-dashed border-border">
                      <span className="text-muted-foreground">Remaining balance on arrival</span>
                      <span className="font-serif text-lg">
                        £{(selectedService?.price - DEPOSIT_AMOUNT).toFixed(0)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground italic">
                      Remaining balance payable on arrival by cash or bank transfer.
                    </p>
                  </div>
                </div>

                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-4">
                    A £20 deposit is required to secure your booking. You will be redirected to our secure payment provider.
                  </p>
                  <div className="bg-muted border border-border p-4 mb-4 text-left" data-testid="cancellation-notice">
                    <p className="text-xs font-medium text-foreground mb-2 tracking-wide uppercase">Cancellation & Rescheduling Policy</p>
                    <ul className="text-xs text-muted-foreground space-y-1 leading-relaxed">
                      <li>• <strong>Cancellations with 48+ hours' notice</strong> — deposit refunded or transferred</li>
                      <li>• <strong>Cancellations with less than 48 hours' notice</strong> — £20 deposit non-refundable</li>
                      <li>• <strong>Rescheduling with 48+ hours' notice</strong> — deposit transferred to new date</li>
                      <li>• <strong>No-show</strong> — £20 deposit non-refundable</li>
                    </ul>
                    <p className="text-xs text-muted-foreground mt-2">
                      To cancel or reschedule, please <a href="mailto:hello@soundhealingmassage.com" className="text-primary underline">get in touch</a>.
                    </p>
                  </div>
                  
                  {/* Terms Agreement Checkbox */}
                  <label className="flex items-start gap-3 text-left mb-6 cursor-pointer" data-testid="terms-checkbox-label">
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-primary"
                      data-testid="terms-checkbox"
                    />
                    <span className="text-xs text-muted-foreground leading-relaxed">
                      I agree to the <a href="/cancellation-policy" target="_blank" className="text-primary underline">cancellation & rescheduling policy</a> and <a href="/terms" target="_blank" className="text-primary underline">terms & conditions</a>. I understand a £20 non-refundable deposit is required and the remaining balance is due on arrival.
                    </span>
                  </label>

                  <Button
                    onClick={handleSubmit}
                    disabled={isLoading || !agreedToTerms}
                    className="w-full rounded-none bg-primary hover:bg-primary/90 text-primary-foreground py-6 text-xs tracking-[0.2em] uppercase"
                    data-testid="pay-now-btn"
                  >
                    {isLoading ? (
                      "Processing..."
                    ) : (
                      <>
                        <CreditCard className="mr-2 w-4 h-4" />
                        Pay £{DEPOSIT_AMOUNT} Deposit
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-12">
            <Button
              variant="outline"
              onClick={() => setCurrentStep(prev => prev - 1)}
              disabled={currentStep === 0}
              className="rounded-none border-border text-muted-foreground hover:text-foreground"
              data-testid="back-btn"
            >
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back
            </Button>
            
            {currentStep < 4 && (
              <Button
                onClick={() => setCurrentStep(prev => prev + 1)}
                disabled={!canProceed()}
                className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground"
                data-testid="next-btn"
              >
                Continue
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
